import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
    FiBell,
    FiCheck,
    FiTrash2,
    FiMoreHorizontal,
    FiEye,
    FiExternalLink,
    FiX,
    FiSettings,
    FiUser,
    FiDollarSign,
    FiBriefcase,
    FiMessageSquare,
    FiStar,
    FiAlertCircle,
    FiGift,
    FiMail
} from 'react-icons/fi'
import { RootState, AppDispatch } from '@/store/store'
import {
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead
} from '@/store/slices/notificationsSlice'
import { Notification, NotificationType } from '@/types/notification'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'

interface NotificationDropdownProps {
    isOpen: boolean
    onClose: () => void
    buttonRef: React.RefObject<HTMLButtonElement>
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
    isOpen,
    onClose,
    buttonRef
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const dropdownRef = useRef<HTMLDivElement>(null)
    const [showActions, setShowActions] = useState<string | null>(null)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [position, setPosition] = useState<{ top: number; left: number; right?: number }>({ top: 0, left: 0 })

    const {
        notifications,
        unreadCount,
        isLoading,
        hasMore,
        currentPage,
        error
    } = useSelector((state: RootState) => state.notifications)

    const { showSuccess, showError } = useSnackbarContext()

    const calculatePosition = () => {
        if (!buttonRef.current) return

        const buttonRect = buttonRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const dropdownWidth = 320 // w-80 = 320px
        const smDropdownWidth = 384 // sm:w-96 = 384px
        const actualDropdownWidth = viewportWidth >= 640 ? smDropdownWidth : dropdownWidth
        const padding = 8 // Minimum padding from viewport edges

        let left: number

        // On small screens (< 640px), center the dropdown
        if (viewportWidth < 640) {
            left = Math.max(padding, Math.min(
                viewportWidth / 2 - actualDropdownWidth / 2,
                viewportWidth - actualDropdownWidth - padding
            ))
        } else {
            // On larger screens, calculate optimal position
            const idealRightAlignLeft = buttonRect.right - actualDropdownWidth
            const idealLeftAlignLeft = buttonRect.left
            const maxAllowedLeft = viewportWidth - actualDropdownWidth - padding
            const minAllowedLeft = padding

            // Try right alignment first (dropdown's right edge aligns with button's right edge)
            if (idealRightAlignLeft >= minAllowedLeft) {
                left = Math.min(idealRightAlignLeft, maxAllowedLeft)
            }
            // Try left alignment (dropdown's left edge aligns with button's left edge)
            else if (idealLeftAlignLeft <= maxAllowedLeft) {
                left = Math.max(idealLeftAlignLeft, minAllowedLeft)
            }
            // Center the dropdown as fallback
            else {
                left = Math.max(minAllowedLeft, Math.min(
                    viewportWidth / 2 - actualDropdownWidth / 2,
                    maxAllowedLeft
                ))
            }
        }

        setPosition({
            top: buttonRect.bottom + 8,
            left: left,
        })
    }

    useEffect(() => {
        if (isOpen) {
            dispatch(fetchNotifications({ page: 1, limit: 10 }))
            calculatePosition()

            const handleResize = () => calculatePosition()
            const handleScroll = () => calculatePosition()

            window.addEventListener('resize', handleResize)
            window.addEventListener('scroll', handleScroll)

            return () => {
                window.removeEventListener('resize', handleResize)
                window.removeEventListener('scroll', handleScroll)
            }
        }
    }, [isOpen, dispatch])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Don't close dropdown if delete dialog is open
            if (showDeleteDialog) return

            if (
                isOpen &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                onClose()
                setShowActions(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, onClose, buttonRef, showDeleteDialog])

    // Handle escape key for delete dialog
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showDeleteDialog) {
                handleCancelDelete()
            }
        }

        if (showDeleteDialog) {
            document.addEventListener('keydown', handleEscapeKey)
        }

        return () => document.removeEventListener('keydown', handleEscapeKey)
    }, [showDeleteDialog])

    const getNotificationIcon = (type: NotificationType) => {
        const iconMap: Record<NotificationType, JSX.Element> = {
            gig_application: <FiBriefcase className="w-4 h-4" />,
            application_status: <FiCheck className="w-4 h-4" />,
            message_received: <FiMessageSquare className="w-4 h-4" />,
            gig_reminder: <FiBell className="w-4 h-4" />,
            payment_received: <FiDollarSign className="w-4 h-4" />,
            payment_sent: <FiDollarSign className="w-4 h-4" />,
            profile_view: <FiUser className="w-4 h-4" />,
            rating_received: <FiStar className="w-4 h-4" />,
            system_update: <FiSettings className="w-4 h-4" />,
            account_security: <FiAlertCircle className="w-4 h-4" />,
            promotional: <FiGift className="w-4 h-4" />,
            welcome: <FiStar className="w-4 h-4" />,
            email_verification: <FiMail className="w-4 h-4" />,
            login_alert: <FiAlertCircle className="w-4 h-4" />,
            password_changed: <FiAlertCircle className="w-4 h-4" />,
            profile_updated: <FiUser className="w-4 h-4" />,
            account_deactivated: <FiAlertCircle className="w-4 h-4" />,
            gig_posted: <FiBriefcase className="w-4 h-4" />,
            gig_updated: <FiBriefcase className="w-4 h-4" />,
            gig_expired: <FiBell className="w-4 h-4" />,
            gig_deleted: <FiTrash2 className="w-4 h-4" />,
            admin_broadcast: <FiSettings className="w-4 h-4" />,
            admin_individual: <FiSettings className="w-4 h-4" />,
            system_maintenance: <FiSettings className="w-4 h-4" />,
            policy_update: <FiSettings className="w-4 h-4" />,
            feature_announcement: <FiGift className="w-4 h-4" />,
            verification: <FiCheck className="w-4 h-4" />,
            document_verification: <FiCheck className="w-4 h-4" />,
            verification_status_changed: <FiCheck className="w-4 h-4" />,
            document_uploaded: <FiCheck className="w-4 h-4" />,
            verification_completed: <FiCheck className="w-4 h-4" />,
            document_rejected: <FiAlertCircle className="w-4 h-4" />,
            document_approved: <FiCheck className="w-4 h-4" />,
            // Phase 1 document verification types
            verification_update: <FiCheck className="w-4 h-4" />,
            document_verified: <FiCheck className="w-4 h-4" />
        }
        return iconMap[type] || <FiBell className="w-4 h-4" />
    }

    const getNotificationColor = (type: NotificationType) => {
        const baseColors: Record<NotificationType, string> = {
            gig_application: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
            application_status: 'text-green-500 bg-green-50 dark:bg-green-900/20',
            message_received: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
            gig_reminder: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
            payment_received: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
            payment_sent: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
            profile_view: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
            rating_received: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20',
            system_update: 'text-gray-500 bg-gray-50 dark:bg-gray-900/20',
            account_security: 'text-red-500 bg-red-50 dark:bg-red-900/20',
            promotional: 'text-pink-500 bg-pink-50 dark:bg-pink-900/20',
            welcome: 'text-green-500 bg-green-50 dark:bg-green-900/20',
            email_verification: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
            login_alert: 'text-red-500 bg-red-50 dark:bg-red-900/20',
            password_changed: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
            profile_updated: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
            account_deactivated: 'text-red-500 bg-red-50 dark:bg-red-900/20',
            gig_posted: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
            gig_updated: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
            gig_expired: 'text-gray-500 bg-gray-50 dark:bg-gray-900/20',
            gig_deleted: 'text-red-500 bg-red-50 dark:bg-red-900/20',
            admin_broadcast: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
            admin_individual: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
            system_maintenance: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
            policy_update: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
            feature_announcement: 'text-green-500 bg-green-50 dark:bg-green-900/20',
            verification: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
            document_verification: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
            verification_status_changed: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20',
            document_uploaded: 'text-green-500 bg-green-50 dark:bg-green-900/20',
            verification_completed: 'text-green-500 bg-green-50 dark:bg-green-900/20',
            document_rejected: 'text-red-500 bg-red-50 dark:bg-red-900/20',
            document_approved: 'text-green-500 bg-green-50 dark:bg-green-900/20',
            // Phase 1 document verification types
            verification_update: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20',
            document_verified: 'text-green-500 bg-green-50 dark:bg-green-900/20'
        }

        return baseColors[type] || 'text-gray-500 bg-gray-50 dark:bg-gray-900/20'
    }

    const getEnhancedNotificationContent = (notification: Notification) => {
        const { type, title, message, data } = notification

        switch (type) {
            case 'message_received':
                return {
                    title: `💬 ${data?.senderName || 'New Message'}`,
                    message: data?.gigTitle ? `About "${data.gigTitle}"` : 'New message received'
                }

            case 'gig_application':
                return {
                    title: `📋 ${data?.senderName || 'New Application'}`,
                    message: data?.gigTitle ? `Applied for "${data.gigTitle}"` : 'New application received'
                }

            case 'application_status':
                const status = data?.status || 'updated'
                return {
                    title: `✅ Application ${status.charAt(0).toUpperCase() + status.slice(1)}`,
                    message: data?.gigTitle ? `"${data.gigTitle}"` : `Application ${status}`
                }

            case 'payment_received':
                return {
                    title: `💰 Payment Received - $${data?.amount || '0'}`,
                    message: data?.gigTitle ? `For "${data.gigTitle}"` : 'Payment received'
                }

            case 'payment_sent':
                return {
                    title: `💸 Payment Sent - $${data?.amount || '0'}`,
                    message: data?.gigTitle ? `For "${data.gigTitle}"` : 'Payment sent'
                }

            case 'rating_received':
                const rating = data?.rating || 0
                const stars = '⭐'.repeat(Math.floor(rating))
                return {
                    title: `⭐ New Rating - ${stars} (${rating}/5)`,
                    message: data?.gigTitle ? `For "${data.gigTitle}"` : 'New rating received'
                }

            case 'login_alert':
                return {
                    title: '🚨 New Login Alert',
                    message: data?.location ? `From ${data.location}` : 'Someone signed into your account'
                }

            case 'verification':
            case 'document_verification':
            case 'verification_status_changed':
                return {
                    title: `📄 ${title}`,
                    message: message || 'Document verification status updated'
                }

            case 'document_uploaded':
                return {
                    title: `📤 Document Uploaded`,
                    message: message || 'Your document has been submitted for verification'
                }

            case 'verification_completed':
                return {
                    title: `✅ Verification Complete`,
                    message: message || 'Your identity verification has been completed'
                }

            case 'document_rejected':
                return {
                    title: `❌ Document Rejected`,
                    message: message || 'Your document was rejected and needs to be resubmitted'
                }

            case 'document_approved':
                return {
                    title: `✅ Document Approved`,
                    message: message || 'Your document has been approved'
                }

            // Phase 1 document verification types
            case 'verification_update':
                const verificationStatus = data?.verificationStatus || 'updated'
                const documentType = data?.documentType ? data.documentType.replace('_', ' ').toUpperCase() : 'document'
                return {
                    title: `📄 Document ${verificationStatus.charAt(0).toUpperCase() + verificationStatus.slice(1)}`,
                    message: message || `Your ${documentType} verification status has been updated`
                }

            case 'document_verified':
                const docType = data?.documentType ? data.documentType.replace('_', ' ').toUpperCase() : 'document'
                return {
                    title: `✅ ${docType} Verified`,
                    message: message || `Your ${docType} has been successfully verified`
                }

            case 'gig_deleted':
                return {
                    title: `🗑️ Gig Deleted`,
                    message: data?.gigTitle ? `"${data.gigTitle}" has been deleted` : 'A gig you were involved with has been deleted'
                }

            default:
                return {
                    title: title,
                    message: message
                }
        }
    }

    const handleNotificationClick = async (notification: Notification) => {
        console.log('🔔 Notification clicked:', JSON.stringify({
            type: notification.type,
            title: notification.title,
            message: notification.message,
            actionUrl: notification.actionUrl,
            data: notification.data
        }, null, 2));

        if (!notification.read) {
            dispatch(markNotificationAsRead(notification._id))
            dispatch(markAsRead(notification._id))
        }

        onClose()

        // Smart navigation based on notification type
        // Prioritize smart navigation over actionUrl for application notifications
        switch (notification.type) {
            case 'message_received':
                if (notification.data?.conversationId) {
                    navigate(`/messages/${notification.data.conversationId}`)
                } else {
                    navigate('/messages')
                }
                break

            case 'gig_application':
                if (notification.data?.applicationId) {
                    navigate(`/dashboard/applications/${notification.data.applicationId}`)
                } else {
                    navigate('/dashboard/applications')
                }
                break

            case 'application_status':
                if (notification.data?.applicationId) {
                    navigate(`/dashboard/applications/${notification.data.applicationId}`)
                } else {
                    navigate('/dashboard/applications')
                }
                break

            case 'payment_received':
            case 'payment_sent':
                navigate('/dashboard/payments')
                break

            case 'rating_received':
                navigate('/dashboard/reviews')
                break

            case 'gig_reminder':
            case 'gig_posted':
            case 'gig_updated':
            case 'gig_expired':
                if (notification.data?.gigId) {
                    navigate(`/gigs/${notification.data.gigId}`)
                } else {
                    navigate('/dashboard/my-gigs')
                }
                break

            case 'profile_view':
                navigate('/dashboard/profile')
                break

            case 'account_security':
            case 'login_alert':
            case 'password_changed':
                navigate('/dashboard/settings?tab=account')
                break

            case 'verification':
            case 'document_verification':
            case 'verification_status_changed':
            case 'document_uploaded':
            case 'verification_completed':
            case 'document_rejected':
            case 'document_approved':
            case 'verification_update':
            case 'document_verified':
                navigate('/dashboard/verification')
                break

            default:
                // For other types, go to notifications page
                navigate('/dashboard/notifications')
                break
        }

        // Fallback to actionUrl for non-application notifications that don't have specific handling
        const handledTypes = [
            'gig_application',
            'application_status',
            'account_security',
            'login_alert',
            'password_changed',
            'verification',
            'document_verification',
            'verification_status_changed',
            'document_uploaded',
            'verification_completed',
            'document_rejected',
            'document_approved',
            'verification_update',
            'document_verified'
        ]

        if (notification.actionUrl && !handledTypes.includes(notification.type)) {
            if (notification.actionUrl.startsWith('/')) {
                navigate(notification.actionUrl)
            } else {
                window.open(notification.actionUrl, '_blank')
            }
            return
        }
    }

    const handleMarkAsRead = async (e: React.MouseEvent, notificationId: string) => {
        e.stopPropagation()
        try {
            dispatch(markNotificationAsRead(notificationId))
            await dispatch(markAsRead(notificationId)).unwrap()
        } catch (error: any) {
            showError(error || 'Failed to mark notification as read')
        }
        setShowActions(null)
    }

    const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
        e.stopPropagation()
        try {
            await dispatch(deleteNotification(notificationId)).unwrap()
            showSuccess('Notification deleted')
        } catch (error: any) {
            showError(error || 'Failed to delete notification')
        }
        setShowActions(null)
    }

    const handleMarkAllAsRead = async () => {
        try {
            // First update local state immediately for better UX
            dispatch(markAllNotificationsAsRead())
            // Then sync with server
            await dispatch(markAllAsRead()).unwrap()
            showSuccess('All notifications marked as read')
        } catch (error: any) {
            showError(error || 'Failed to mark all notifications as read')
            // Refetch to get the correct state
            dispatch(fetchNotifications({ page: 1, limit: 10 }))
        }
    }

    const handleDeleteAllClick = () => {
        setShowDeleteDialog(true)
    }

    const handleConfirmDeleteAll = async () => {
        setShowDeleteDialog(false)
        try {
            await dispatch(deleteAllNotifications()).unwrap()
            showSuccess('All notifications deleted')
        } catch (error: any) {
            showError(error || 'Failed to delete all notifications')
        }
    }

    const handleCancelDelete = () => {
        setShowDeleteDialog(false)
    }

    const loadMore = () => {
        if (!isLoading && hasMore) {
            dispatch(fetchNotifications({ page: currentPage + 1, limit: 10 }))
        }
    }

    if (!isOpen) return null

    const dropdownContent = (
        <div
            ref={dropdownRef}
            className="fixed w-80 sm:w-96 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-96 flex flex-col"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                    <FiBell className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Notifications
                    </h3>
                    {unreadCount > 0 && (
                        <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-medium px-2 py-1 rounded-full">
                            {unreadCount}
                        </span>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                >
                    <FiX className="w-5 h-5" />
                </button>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex items-center gap-2 text-sm">
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllAsRead}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                            <FiCheck className="w-4 h-4" />
                            Mark all read
                        </button>
                    )}
                    {notifications.length > 0 && (
                        <>
                            {unreadCount > 0 && (
                                <span className="text-gray-300 dark:text-gray-600">•</span>
                            )}
                            <button
                                onClick={handleDeleteAllClick}
                                className="flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                                <FiTrash2 className="w-4 h-4" />
                                Clear all
                            </button>
                        </>
                    )}
                </div>
                <Link
                    to="/dashboard/notifications"
                    onClick={onClose}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    View all
                    <FiExternalLink className="w-3 h-3" />
                </Link>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
                {error ? (
                    <div className="flex flex-col items-center justify-center p-8 text-gray-500 dark:text-gray-400">
                        <FiAlertCircle className="w-12 h-12 mb-4 text-red-400 dark:text-red-500" />
                        <p className="text-center text-red-600 dark:text-red-400 font-medium">Failed to load notifications</p>
                        <p className="text-sm text-center mt-1">
                            {error}
                        </p>
                        <button
                            onClick={() => dispatch(fetchNotifications({ page: 1, limit: 10 }))}
                            className="mt-4 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Try again
                        </button>
                    </div>
                ) : isLoading && notifications.length === 0 ? (
                    <div className="flex items-center justify-center p-8">
                        <LoadingSpinner />
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-8 text-gray-500 dark:text-gray-400">
                        <FiBell className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" />
                        <p className="text-center font-medium">No notifications yet</p>
                        <p className="text-sm text-center mt-1 mb-4">
                            You'll see updates about your gigs and applications here
                        </p>
                        <Link
                            to="/dashboard/notifications"
                            onClick={onClose}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                        >
                            View all notifications
                            <FiExternalLink className="w-3 h-3" />
                        </Link>
                    </div>
                ) : (
                    <div>
                        {(() => {
                            const displayNotifications = notifications.slice(0, 10)
                            const unreadNotifications = displayNotifications.filter(n => !n.read)
                            const readNotifications = displayNotifications.filter(n => n.read)

                            return (
                                <>
                                    {/* Unread Notifications */}
                                    {unreadNotifications.length > 0 && (
                                        <div>
                                            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                                                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                                    Unread ({unreadNotifications.length})
                                                </h4>
                                            </div>
                                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                {unreadNotifications.map((notification) => {
                                                    const enhancedContent = getEnhancedNotificationContent(notification)
                                                    return (
                                                        <div
                                                            key={notification._id}
                                                            className="relative p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors bg-blue-50/50 dark:bg-blue-900/10 group"
                                                            onClick={() => handleNotificationClick(notification)}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                {/* Icon */}
                                                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getNotificationColor(notification.type)}`}>
                                                                    {getNotificationIcon(notification.type)}
                                                                </div>

                                                                {/* Content */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-start justify-between">
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                                {enhancedContent.title}
                                                                            </p>
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                                                                {enhancedContent.message}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                                                                                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                                                            </p>
                                                                        </div>

                                                                        {/* Actions Button */}
                                                                        <div className="flex-shrink-0 ml-2">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation()
                                                                                    setShowActions(showActions === notification._id ? null : notification._id)
                                                                                }}
                                                                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            >
                                                                                <FiMoreHorizontal className="w-4 h-4" />
                                                                            </button>

                                                                            {/* Actions Dropdown */}
                                                                            {showActions === notification._id && (
                                                                                <div className="absolute right-4 top-12 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                                                                                    <button
                                                                                        onClick={(e) => handleMarkAsRead(e, notification._id)}
                                                                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-lg"
                                                                                    >
                                                                                        <FiEye className="w-4 h-4" />
                                                                                        Mark as read
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={(e) => handleDeleteNotification(e, notification._id)}
                                                                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg"
                                                                                    >
                                                                                        <FiTrash2 className="w-4 h-4" />
                                                                                        Delete
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Unread indicator */}
                                                                    <div className="absolute top-4 right-4 w-2 h-2 bg-blue-500 rounded-full"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Read Notifications */}
                                    {readNotifications.length > 0 && (
                                        <div>
                                            {unreadNotifications.length > 0 && (
                                                <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                                                    <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                                        Read ({readNotifications.length})
                                                    </h4>
                                                </div>
                                            )}
                                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                {readNotifications.map((notification) => {
                                                    const enhancedContent = getEnhancedNotificationContent(notification)
                                                    return (
                                                        <div
                                                            key={notification._id}
                                                            className="relative p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors group"
                                                            onClick={() => handleNotificationClick(notification)}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                {/* Icon */}
                                                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getNotificationColor(notification.type)}`}>
                                                                    {getNotificationIcon(notification.type)}
                                                                </div>

                                                                {/* Content */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-start justify-between">
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                                {enhancedContent.title}
                                                                            </p>
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                                                                {enhancedContent.message}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                                                                                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                                                            </p>
                                                                        </div>

                                                                        {/* Actions Button */}
                                                                        <div className="flex-shrink-0 ml-2">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation()
                                                                                    setShowActions(showActions === notification._id ? null : notification._id)
                                                                                }}
                                                                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            >
                                                                                <FiMoreHorizontal className="w-4 h-4" />
                                                                            </button>

                                                                            {/* Actions Dropdown */}
                                                                            {showActions === notification._id && (
                                                                                <div className="absolute right-4 top-12 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                                                                                    <button
                                                                                        onClick={(e) => handleDeleteNotification(e, notification._id)}
                                                                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                                                                    >
                                                                                        <FiTrash2 className="w-4 h-4" />
                                                                                        Delete
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Load More */}
                                    {hasMore && notifications.length >= 10 && (
                                        <div className="p-4 text-center border-t border-gray-200 dark:border-gray-700">
                                            <button
                                                onClick={loadMore}
                                                disabled={isLoading}
                                                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                                            >
                                                {isLoading ? 'Loading...' : 'Load more'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )
                        })()}
                    </div>
                )}
            </div>
        </div>
    )

    const deletionDialog = showDeleteDialog && (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
            onClick={handleCancelDelete}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0">
                            <FiAlertCircle className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Delete All Notifications
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Are you sure you want to delete all notifications? This action cannot be undone.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            onClick={handleCancelDelete}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirmDeleteAll}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                        >
                            Delete All
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )

    return (
        <>
            {createPortal(dropdownContent, document.body)}
            {deletionDialog && createPortal(deletionDialog, document.body)}
        </>
    )
}

export default NotificationDropdown